// App.jsx
import React from "react";
import { useForm } from "react-hook-form";
import {
  FiShield,
  FiHome,
  FiUsers,
  FiCheckCircle,
  FiTool,
  FiBarChart2,
  FiFileText,
  FiTrendingUp,
  FiPhone,
  FiMail,
  FiArrowRight,
} from "react-icons/fi";

const Container = ({ children, className = "" }) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const Badge = ({ children }) => (
  <span className="inline-flex uppercase items-center rounded-full border border-[var(--border)] bg-[var(--card)] px-3 py-1 text-xs font-medium text-[var(--text)]">
    {children}
  </span>
);

const SectionTitle = ({ eyebrow, title, description }) => (
  <div className="max-w-2xl">
    {eyebrow ? (
      <div className="mb-3">
        <Badge>{eyebrow}</Badge>
      </div>
    ) : null}
    <h2 className="text-3xl font-semibold tracking-tight text-[var(--text)] sm:text-4xl">
      {title}
    </h2>
    {description ? (
      <p className="mt-4 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
        {description}
      </p>
    ) : null}
  </div>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-[var(--border)] bg-white 0 p-6 shadow-sm ${className}`}
  >
    {children}
  </div>
);

const IconBubble = ({ icon: Icon }) => (
  <div className="flex shrink-0 w-[40px] h-[40px] aspect-square items-center justify-center rounded-full border border-[var(--border)] bg-[var(--card)] text-[var(--primary)]">
    <Icon className="text-xl" />
  </div>
);

const Button = ({
  children,
  href,
  variant = "primary",
  className = "",
  onClick,
  type = "button",
}) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:ring-offset-2 focus:ring-offset-[var(--bg)]";
  const styles =
    variant === "primary"
      ? "bg-[var(--primary)] text-white hover:brightness-110"
      : "border border-[var(--border)] bg-[var(--card)] text-[var(--text)] hover:bg-white/60";
  const Comp = href ? "a" : "button";
  return (
    <Comp
      href={href}
      onClick={onClick}
      type={Comp === "button" ? type : undefined}
      className={`${base} ${styles} ${className}`}
    >
      {children}
    </Comp>
  );
};

const NavLink = ({ href, children }) => (
  <a
    href={href}
    className="text-sm font-medium text-[var(--muted)] hover:text-[var(--text)] transition"
  >
    {children}
  </a>
);

export default function App() {
  const { register, handleSubmit, formState, reset } = useForm({
    defaultValues: { name: "", email: "", phone: "", message: "" },
  });

  const onSubmit = async (data) => {
    // Replace with your API call
    console.log("Lead:", data);
    reset();
    alert("Submitted. Replace this with your backend integration.");
  };

  const nav = [
    { label: "Services", href: "#services" },
    { label: "Who We Serve", href: "#who-we-serve" },
    { label: "Why Us", href: "#why" },
    { label: "Governance", href: "#governance" },
    { label: "Certifications", href: "#certifications" },
    { label: "Experience", href: "#experience" },
    { label: "Leadership", href: "#leadership" },
    { label: "Contact", href: "#contact" },
  ];

  const whoWeServe = [
    { icon: FiHome, title: "Project-Based Section 8 (PBRA) properties" },
    { icon: FiFileText, title: "Affordable and income-restricted housing" },
    { icon: FiUsers, title: "Senior housing communities" },
    { icon: FiShield, title: "Government-owned or government-supported housing assets" },
  ];

  const pillars = [
    {
      icon: FiShield,
      title: "HUD Compliance and Program Administration",
      items: [
        "Full compliance with HUD Handbook 4350.3, HOTMA, and HAP Contract requirements",
        "Accurate certification and recertification management",
        "Secure EIV monitoring and data integrity oversight",
        "Audit-ready documentation and reporting",
      ],
    },
    {
      icon: FiUsers,
      title: "Leasing, Occupancy and Resident Stability",
      items: [
        "Tenant Selection Plan compliant leasing",
        "Occupancy optimization and vacancy minimization",
        "Resident retention strategies",
        "Fair Housing, VAWA, and Section 504 compliance",
      ],
    },
    {
      icon: FiTool,
      title: "Maintenance and Physical Asset Management",
      items: [
        "Preventive and corrective maintenance coordination",
        "NSPIRE-aligned inspection readiness",
        "Life-safety and habitability oversight",
        "Vendor and contractor management",
      ],
    },
    {
      icon: FiBarChart2,
      title: "Financial and Subsidy Administration",
      items: [
        "HAP billing and subsidy reconciliation",
        "TRACS reporting and error resolution",
        "Rent, utility allowance, and subsidy accuracy",
        "Financial performance visibility",
      ],
    },
  ];

  const whyUs = [
    { icon: FiCheckCircle, title: "Zero tolerance for compliance gaps" },
    { icon: FiTrendingUp, title: "Continuous monitoring of regulatory risk" },
    { icon: FiShield, title: "Structured governance and accountability" },
    { icon: FiBarChart2, title: "Protection of subsidy flow and asset value" },
    { icon: FiHome, title: "Long-term operational stability" },
  ];

  const reportingAreas = [
    "Occupancy and leasing performance",
    "Certification and recertification status",
    "HUD compliance indicators",
    "Maintenance performance and NSPIRE readiness",
    "Issue tracking and resolution",
  ];

  const certifications = [
    "Certified Occupancy Specialist (COS)",
    "HUD ACE Certification",
    "NSPIRE Certification",
    "Management and Occupancy Reviews (MOR)",
  ];

  const leadership = [
    {
      name: "Himanshu Agrawal",
      role: "President",
      description:
        "Leadership with experience across real estate, finance, compliance, and systems integration.",
      imageAlt: "Leadership photo placeholder",
      img: "/img/himanshu.jpg",
    },
    {
      name: "Anastasia Agrawal",
      role: "Tenant Services and Engagement Lead",
      description:
        "Resident experience focus supporting stable communities and consistent communication.",
      imageAlt: "Leadership photo placeholder",
      img: "/img/anastasia.jpg",
    },
    {
      name: "Artem Smirnov",
      role: "HUD Standards and Inspections Manager",
      description:
        "Inspection readiness and physical standards oversight aligned with HUD requirements.",
      imageAlt: "Leadership photo placeholder",
      img: "/img/artem.jpg",
    },
  ];

  return (
    <div className="min-h-screen bg-[radial-gradient(60%_50%_at_50%_0%,rgba(234,88,12,0.05),transparent_60%)] text-[var(--text)]">
      {/* Background */}
      

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[var(--border)] bg-[var(--bg)]/80 backdrop-blur">
        <Container className="flex h-16 items-center justify-between">
          <a href="#top" className="flex items-center gap-2">
            <img src="/img/h-logo.png" alt="Kulfi Housing Logo" className=" w-[200px]" />
          </a>

          <nav className="hidden items-center gap-6 lg:flex">
            {nav.map((n) => (
              <NavLink key={n.href} href={n.href}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* <div className="flex items-center gap-3">
            <Button href="#contact" variant="secondary" className="hidden sm:inline-flex">
              Contact
            </Button>
            <Button href="#contact" variant="primary">
              Request Overview <FiArrowRight className="h-4 w-4" />
            </Button>
          </div> */}
        </Container>
      </header>

      {/* Hero */}
      <main id="top">
        <section className="pt-14 pb-10 sm:pt-18 sm:pb-14">
          <Container>
            <div className="grid items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <div className="flex flex-wrap gap-2">
                  <Badge>HUD property management</Badge>
                  <Badge>Section 8 property management</Badge>
                  <Badge>PBRA management company</Badge>
                </div>

                <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl">
                  HUD-Compliant Property Management for Affordable Housing
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                  End-to-end management services for Project-Based Section 8 and
                  affordable housing communities, focused on compliance, stability,
                  and long-term asset performance.
                </p>

                <div className="mt-7 flex flex-wrap gap-3">
                  <Button href="#contact" variant="primary">
                    Request Management Capability Overview{" "}
                    <FiArrowRight className="h-4 w-4" />
                  </Button>
                  <Button href="#contact" variant="secondary">
                    Contact Our Team
                  </Button>
                </div>

                <div className="mt-8 grid gap-3 sm:grid-cols-3">
                  {[
                    { icon: FiShield, label: "Compliance-first operations" },
                    { icon: FiFileText, label: "Audit-ready reporting" },
                    { icon: FiHome, label: "Resident stability focus" },
                  ].map((x) => (
                    <div
                      key={x.label}
                      className="flex items-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4"
                    >
                      <IconBubble icon={x.icon} />
                      <div className="text-sm font-medium">{x.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5">
                <Card className="p-5">
                  <div className="rounded-2xl border border-[var(--border)]">
                    <div className="aspect-[1/1.1] w-full overflow-hidden rounded-xl bg-[url(img/hero.jpg)] bg-cover" />

                  </div>

                  <div className="mt-5 grid gap-3">
                    <div className="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
                      <FiPhone className="h-5 w-5 text-[var(--primary)]" />
                      <div className="text-sm">
                        <div className="font-semibold">Call</div>
                        <div className="text-[var(--muted)]">+1 (551) 225-0733</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
                      <FiMail className="h-5 w-5 text-[var(--primary)]" />
                      <div className="text-sm">
                        <div className="font-semibold">Email</div>
                        <div className="text-[var(--muted)]">acquisition@kufihousing.com</div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </Container>
        </section>

        {/* Who We Serve */}
        <section id="who-we-serve" className="py-14 sm:py-18">
          <Container>
            <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
              <SectionTitle
                eyebrow="Who we serve"
                title="Specialized Management for HUD-Regulated Properties"
                description="Kulfi Housing provides professional property management services exclusively for affordable housing communities operating under HUD programs."
              />
              <div className="max-w-xl text-sm text-[var(--muted)]">
                We understand the regulatory complexity, compliance exposure, and operational
                discipline required to successfully manage HUD-assisted properties.
              </div>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {whoWeServe.map((x) => (
                <Card key={x.title}>
                  <IconBubble icon={x.icon} />
                  <div className="mt-4 text-base font-semibold">{x.title}</div>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Services */}
        <section id="services" className="py-14 sm:py-18">
          <Container>
            <SectionTitle
              eyebrow="Services"
              title="Our Property Management Services"
              description="We deliver outcomes that owners and agencies care about: compliance, stability, inspection readiness, accurate subsidy administration, and performance visibility."
            />

            <div className="mt-10 grid gap-6 lg:grid-cols-2">
              {pillars.map((p) => (
                <Card key={p.title}>
                  <div className="flex items-start gap-4">
                    <IconBubble icon={p.icon} />
                    <div>
                      <div className="text-lg font-semibold">{p.title}</div>
                      <ul className="mt-4 space-y-2 text-sm text-[var(--muted)]">
                        {p.items.map((it) => (
                          <li key={it} className="flex  gap-2">
                            <FiCheckCircle className=" shrink-0 mt-0.5 h-4 w-4 text-[var(--primary)]" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-10 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6 sm:p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-lg font-semibold">
                    Request a Management Capability Overview
                  </div>
                  <div className="mt-1 text-sm text-[var(--muted)]">
                    Share your property details and we will respond with relevant capability information.
                  </div>
                </div>
                <Button href="#contact" variant="primary" className="sm:shrink-0">
                  Request Overview <FiArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Container>
        </section>

        {/* Why Kulfi Housing */}
        <section id="why" className="py-14 sm:py-18">
          <Container>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-5">
                <SectionTitle
                  eyebrow="Why Kulfi Housing"
                  title="A Compliance-First Management Partner"
                  description="Kulfi Housing operates with a compliance-first mindset to reduce risk, protect public funds, and maintain stable housing environments for residents."
                />
              </div>

              <div className="lg:col-span-7">
                <div className="grid gap-5 sm:grid-cols-2">
                  {whyUs.map((x) => (
                    <Card key={x.title}>
                      <div className="flex items-start gap-4">
                        <IconBubble icon={x.icon} />
                        <div className="text-base font-semibold">{x.title}</div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Governance */}
        <section id="governance" className="py-14 sm:py-18">
          <Container>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-6">
                <SectionTitle
                  eyebrow="Governance"
                  title="Transparent Oversight and Performance Reporting"
                  description="Our management structure is designed to provide ownership and stakeholders with clear visibility into property performance without interfering in daily operations."
                />
                <div className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6">
                  <div className="flex items-center gap-3">
                    <IconBubble icon={FiFileText} />
                    <div>
                      <div className="text-sm font-semibold">Performance visibility</div>
                      <div className="text-sm text-[var(--muted)]">
                        Structured reporting supports accountability and continuous improvement.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-6">
                <Card>
                  <div className="text-lg font-semibold">Key Reporting Areas</div>
                  <ul className="mt-4 space-y-3 text-sm text-[var(--muted)]">
                    {reportingAreas.map((it) => (
                      <li key={it} className="flex gap-2">
                        <FiCheckCircle className="mt-0.5 h-4 w-4 text-[var(--primary)]" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </div>
          </Container>
        </section>

        {/* Certifications */}
        <section id="certifications" className="py-14 sm:py-18">
          <Container>
            <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
              <SectionTitle
                eyebrow="Certifications"
                title="Certified Affordable Housing Professionals"
                description="Our team maintains current certifications and ongoing training to stay aligned with evolving HUD requirements."
              />
              <div className="text-sm text-[var(--muted)]">
                SEO terms: HUD-certified property management, NSPIRE compliance, MOR-ready management
              </div>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {certifications.map((c) => (
                <Card key={c}>
                  <IconBubble icon={FiShield} />
                  <div className="mt-4 text-base font-semibold">{c}</div>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Experience */}
        <section id="experience" className="py-14 sm:py-18">
          <Container>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <SectionTitle
                  eyebrow="Experience"
                  title="Proven Experience in HUD-Regulated Environments"
                  description="Our leadership and management team brings hands-on experience from managing and supporting large-scale affordable housing communities, including multi-hundred-unit Section 8 properties."
                />
                <p className="mt-6 text-base leading-relaxed text-[var(--muted)]">
                  We combine regulatory expertise, operational discipline, and resident-focused
                  management to support sustainable, compliant housing operations.
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {[
                    { icon: FiShield, title: "Regulatory expertise", text: "Aligned with HUD standards and documentation requirements." },
                    { icon: FiTool, title: "Operational discipline", text: "Consistency across compliance, leasing, and maintenance." },
                    { icon: FiUsers, title: "Resident-focused", text: "Stability, communication, and service quality." },
                    { icon: FiBarChart2, title: "Performance visibility", text: "Clear reporting for owners and stakeholders." },
                  ].map((x) => (
                    <Card key={x.title}>
                      <div className="flex items-start gap-4">
                        <IconBubble icon={x.icon} />
                        <div>
                          <div className="text-base font-semibold">{x.title}</div>
                          <div className="mt-1 text-sm text-[var(--muted)]">{x.text}</div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5">
                <Card className="p-5">
                  <div className="rounded-2xl border border-[var(--border)] bg-white/60">
                    <div className="aspect-[3/4] w-full overflow-hidden rounded-xl bg-[url(https://www.outsourcinghubindia.com/wp-content/uploads/2018/05/HUD-multifamily-housing-programs.jpg)] bg-cover" />
                    
                  </div>
                </Card>
              </div>
            </div>
          </Container>
        </section>

        {/* Leadership */}
        <section id="leadership" className="py-14 sm:py-18">
          <Container>
            <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
              <SectionTitle
                eyebrow="Leadership"
                title="Leadership with Cross-Disciplinary Expertise"
                description="Kulfi Housing is led by professionals with backgrounds in real estate, finance, compliance, and systems integration, enabling a well-rounded and risk-aware management approach."
              />
              <Button href="#contact" variant="secondary" className="w-fit">
                Meet Our Leadership Team <FiArrowRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {leadership.map((m) => (
                <Card key={m.name} className="overflow-hidden p-0">
                  <div className={`aspect-[1/1] rounded-xl w-full bg-[url(${m.img})] bg-cover`} aria-label={m.imageAlt} />
                  <div className="py-4">
                    <div className="text-base font-semibold">{m.name}</div>
                    <div className="mt-1 text-sm font-medium text-[var(--primary)]">{m.role}</div>
                    <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">
                      {m.description}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Final CTA + Contact */}
        <section id="contact" className="py-14 sm:py-18">
          <Container>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-5">
                <SectionTitle
                  eyebrow="Contact"
                  title="Looking for a HUD-Compliant Property Management Partner?"
                  description="If you are seeking a management company that prioritizes compliance, transparency, and long-term stability for affordable housing assets, Kulfi Housing is prepared to support your property."
                />

                <div className="mt-8 grid gap-4">
                  <Card>
                    <div className="flex items-start gap-4">
                      <IconBubble icon={FiPhone} />
                      <div>
                        <div className="text-sm font-semibold">Phone</div>
                        <div className="text-sm text-[var(--muted)]">+1 (551) 225-0733</div>
                      </div>
                    </div>
                  </Card>
                  <Card>
                    <div className="flex items-start gap-4">
                      <IconBubble icon={FiMail} />
                      <div>
                        <div className="text-sm font-semibold">Email</div>
                        <div className="text-sm text-[var(--muted)]">acquisition@kufihousing.com</div>
                      </div>
                    </div>
                  </Card>
                </div>

              
              </div>

              <div className="lg:col-span-7">
                <Card className="p-6 sm:p-8">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div className="text-xl font-semibold">Request a Proposal</div>
                      <p className="mt-2 text-sm text-[var(--muted)]">
                        Submit your property details. Replace the submit handler with your backend.
                      </p>
                    </div>
                    <div className="hidden sm:block">
                      <IconBubble icon={FiFileText} />
                    </div>
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)} className="mt-6 grid gap-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium">Full Name</label>
                        <input
                          className="mt-2 w-full rounded-xl border border-[var(--border)] bg-white/70 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
                          placeholder="Your name"
                          {...register("name", { required: true })}
                        />
                        {formState.errors.name ? (
                          <div className="mt-1 text-xs text-red-600">Name is required.</div>
                        ) : null}
                      </div>

                      <div>
                        <label className="text-sm font-medium">Email</label>
                        <input
                          className="mt-2 w-full rounded-xl border border-[var(--border)] bg-white/70 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
                          placeholder="you@company.com"
                          {...register("email", {
                            required: true,
                            pattern: /^\S+@\S+\.\S+$/,
                          })}
                        />
                        {formState.errors.email ? (
                          <div className="mt-1 text-xs text-red-600">Valid email is required.</div>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium">Phone (optional)</label>
                      <input
                        className="mt-2 w-full rounded-xl border border-[var(--border)] bg-white/70 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
                        placeholder="+1 (___) ___-____"
                        {...register("phone")}
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium">Message</label>
                      <textarea
                        rows={5}
                        className="mt-2 w-full resize-none rounded-xl border border-[var(--border)] bg-white/70 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
                        placeholder="Tell us about the property, units, and timeline."
                        {...register("message", { required: true, minLength: 10 })}
                      />
                      {formState.errors.message ? (
                        <div className="mt-1 text-xs text-red-600">
                          Message must be at least 10 characters.
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <Button type="submit" variant="primary">
                        Request Proposal <FiArrowRight className="h-4 w-4" />
                      </Button>
                      <Button href="mailto:acquisition@kufihousing.com" variant="secondary">
                        Email Our Team
                      </Button>
                      <div className="text-xs text-[var(--muted)]">
                        By submitting, you agree to be contacted about this request.
                      </div>
                    </div>
                  </form>
                </Card>
              </div>
            </div>
          </Container>
        </section>

        {/* Footer */}
        <footer className="border-t border-[var(--border)] py-10">
          <Container>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-[var(--muted)]">
                © {new Date().getFullYear()} Kulfi Housing. All rights reserved.
              </div>
              <div className="flex flex-wrap gap-5 text-sm">
                {nav.map((n) => (
                  <NavLink key={n.href} href={n.href}>
                    {n.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </Container>
        </footer>
      </main>
    </div>
  );
}
